import React, { Fragment } from 'react';
import NProgress from 'nprogress';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import Loadable from 'react-loadable';
import Loader from '../components/LayoutComponents/Loader';
import AuthLayout from './Auth';
import EmbeddedLayout from './Embedded';
import MenuCloseOverlay from '../components/LayoutComponents/MenuCloseOverlay';

// Lazy-load MainLayout so admin-only chrome (TopBar/SideMenu/Settings and their
// transitive imports of pages/fleet/*) ships in its own chunk instead of main.js.
// /inspection and /auth/* never render MainLayout and won't fetch this chunk.
const MainLayout = Loadable({
  loader: () => import(/* webpackChunkName: "main-layout" */ './Main'),
  loading: () => <Loader />,
  delay: false,
});

// Same for the standalone /help layout (it pulls in ProfileMenu and react-bootstrap).
const HelpLayout = Loadable({
  loader: () => import(/* webpackChunkName: "help-layout" */ './Help'),
  loading: () => <Loader />,
  delay: false,
});

// Login card over the public Help Center, shown on /help when logged out.
const HelpLogin = Loadable({
  loader: () => import(/* webpackChunkName: "help-login" */ '../pages/help/HelpLogin'),
  loading: () => <Loader />,
  delay: false,
});

const Layouts = {
  auth: AuthLayout,
  main: MainLayout,
  embedded: EmbeddedLayout,
  oauth: AuthLayout,
  help: HelpLayout,
};

@withRouter
@connect(({ auth }) => ({ auth }))
class IndexLayout extends React.PureComponent {
  previousPath = '';

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    const { prevLocation } = prevProps;
    if (location !== prevLocation) {
      window.scrollTo(0, 0);
    }
  }

  render() {
    const {
      children,
      // loading,
      location: { pathname, search },
      auth,
    } = this.props;

    // NProgress Management
    const currentPath = pathname + search;
    if (currentPath !== this.previousPath /* || loading.global */) {
      NProgress.start();
    }

    // if (!loading.global) {
    setTimeout(() => {
      NProgress.done();
      this.previousPath = currentPath;
    }, 300);
    // }

    // Layout Rendering
    const getLayout = () => {
      if (/^\/auth(?=\/|$)/i.test(pathname)) {
        return 'auth';
      }
      if (/^\/inspection(?=[-/]|$)/i.test(pathname)) {
        return 'embedded';
      }
      if (/^\/oauth(?=\/|$)/i.test(pathname)) {
        return 'oauth';
      }
      if (/^\/help(?=\/|$)/i.test(pathname)) {
        return 'help';
      }

      return 'main';
    };

    const Container = Layouts[getLayout()];

    const isUserAuthorized = auth.authorized;
    const isUserLoading = auth.loading;
    const isAuthLayout = getLayout() === 'auth';
    const isMainLayout = getLayout() === 'main';
    const isEmbeddedLayout = getLayout() === 'embedded';
    const isHelpLayout = getLayout() === 'help';
    // OAuth consent page must render the same way regardless of any existing Duet
    // session in this browser — never redirect it to /auth/login or away to /dashboard.
    const isOauthLayout = getLayout() === 'oauth';

    const BootstrappedLayout = () => {
      // show loader when user in check authorization process, not authorized yet and not on login pages
      if (isUserLoading && !isUserAuthorized && !isAuthLayout && !isEmbeddedLayout && !isOauthLayout) {
        return <Loader />;
      }

      // logged out on /help: show the login card over the public Help Center instead of
      // redirecting, so the requested page (?page=...) opens right after login
      if (isHelpLayout && !isUserAuthorized) {
        return <HelpLogin />;
      }

      // redirect to login page if current is not login page and user not authorized
      if (!isAuthLayout && !isEmbeddedLayout && !isOauthLayout && !isUserAuthorized) {
        return <Redirect to="/auth/login" />;
      }

      // redirect to main dashboard when user on auth page and authorized
      if (isAuthLayout && isUserAuthorized) {
        if (auth.is_admin.includes(1) || auth.is_admin.includes(3)) return <Redirect to="/dashboard" />;
        return <Redirect to="/rider-services/bookings" />;
      }

      // in other case render previously set layout
      return (
        <Container>
          {children}
          <MenuCloseOverlay isMainLayout={isMainLayout} />
        </Container>
      );
    };

    return (
      <Fragment>
        <Helmet titleTemplate="Duet MaaS Client WebApp %s" title=" " />
        {BootstrappedLayout()}
      </Fragment>
    );
  }
}

IndexLayout.defaultProps = {
  user: {
    authorized: false,
  },
};

export default IndexLayout;
